import { FieldDeposit, ProspectingEvent } from "./types.ts";
import { parseCgtToUnix } from "./utils.ts";

const parser = new DOMParser();

export function parseString(xmlString: string): Document {
  return parser.parseFromString(xmlString, "text/xml");
}

export function extractLayout(xml: Document): string {
  const layout = xml.getElementsByTagName("layout");
  if (layout.length > 0) {
    return (layout[0].textContent ?? "").replace(/\s/g, "");
  }

  const fieldString = xml.getElementsByTagName("fieldString");
  if (fieldString.length > 0) {
    return (fieldString[0].textContent ?? "").replace(/\s/g, "");
  }

  throw new Error("No layout found");
}

export function extractGalaxyCoordinates(xml: Document): { x: number; y: number } | null {
  const root = xml.getElementsByTagName("asteroid-prospecting").item(0);
  if (!root) {
    return null;
  }

  const galX = root.getAttribute("galX");
  const galY = root.getAttribute("galY");
  if (galX === null || galY === null) {
    return null;
  }

  return {
    x: Number(galX),
    y: Number(galY),
  };
}

function mergeScans(
  scans: Array<{ date: number; x: number; y: number; result: number }>,
): ProspectingEvent[] {
  const byDate = scans.reduce<Record<string, Array<{ date: number; x: number; y: number; result: number }>>>((acc, scan) => {
    const key = String(scan.date);
    acc[key] = acc[key] || [];
    acc[key].push(scan);
    return acc;
  }, {});

  return Object.keys(byDate).map((dateKey) => {
    const dateScans = byDate[dateKey];
    const firstScan = dateScans[0];
    const result: ProspectingEvent = {
      date: Number(dateKey),
      system: {
        x: firstScan.x,
        y: firstScan.y,
      },
      trace: firstScan.result >= 0 ? firstScan.result : -1,
      deposit: firstScan.result === -2 ? { type: "", size: 0 } : undefined,
    };

    if (dateScans.length > 1) {
      const otherScan = dateScans[1];
      if (otherScan.result > 0) {
        result.trace = otherScan.result;
      } else if (otherScan.result === -1 && !result.trace) {
        result.trace = -1;
      } else if (otherScan.result === -2) {
        result.deposit = { type: "", size: 0 };
      }
    }

    return result;
  });
}

function groupDepositsByCoords(xml: Document): Record<string, { type: string; size: number }> {
  const deposits = Array.from(xml.getElementsByTagName("deposit"));

  return deposits.reduce<Record<string, { type: string; size: number }>>((acc, deposit) => {
    const x = deposit.getAttribute("x") ?? "0";
    const y = deposit.getAttribute("y") ?? "0";
    acc[`(${x},${y})`] = {
      type: `16:${deposit.getAttribute("type_code")}`,
      size: Number(deposit.getElementsByTagName("size")[0]?.textContent ?? 0),
    };
    return acc;
  }, {});
}

function attachDeposits(events: ProspectingEvent[], depositsByCoords: Record<string, { type: string; size: number }>): ProspectingEvent[] {
  return events.map((event) => ({
    ...event,
    deposit: depositsByCoords[`(${event.system.x},${event.system.y})`],
  }));
}

function attachGalacticCoords(xml: Document, events: ProspectingEvent[]): ProspectingEvent[] {
  const root = xml.getElementsByTagName("asteroid-prospecting").item(0);
  if (events.length > 0 && !root) {
    throw new Error("Cannot find galactic coordinates for events");
  }
  if (events.length === 0) {
    return [];
  }

  const galX = Number(root?.getAttribute("galX") ?? 0);
  const galY = Number(root?.getAttribute("galY") ?? 0);

  return events.map((event) => ({ ...event, galaxy: { x: galX, y: galY } }));
}

export function transformScansIntoEvents(xml: Document): ProspectingEvent[] {
  const scanElements = Array.from(xml.getElementsByTagName("scan"));

  const scans = scanElements.map((scan) => {
    const date = parseCgtToUnix(scan.getAttribute("date") ?? "");
    const x = Number(scan.getAttribute("x") ?? 0);
    const y = Number(scan.getAttribute("y") ?? 0);
    const result = Number(scan.getAttribute("result") ?? -1);
    return { date, x, y, result };
  });

  const events = mergeScans(scans);
  const depositsByCoords = groupDepositsByCoords(xml);
  const withGalacticCoords = attachGalacticCoords(xml, events);

  return attachDeposits(withGalacticCoords, depositsByCoords);
}

export function extractDeposits(xml: Document): FieldDeposit[] {
  return Array.from(xml.getElementsByTagName("deposit")).map((elem) => {
    const x = Number(elem.getAttribute("x") ?? 0);
    const y = Number(elem.getAttribute("y") ?? 0);
    const type = `16:${elem.getAttribute("type_code")}`;
    const size = Number(elem.getElementsByTagName("size")[0]?.textContent ?? 0);
    const stability = elem.getElementsByTagName("stability")[0]?.getAttribute("title") || null;

    return { x, y, type, size, stability };
  });
}
